import type { Background, BackgroundTable } from '../content/types.ts';

/**
 * Backgrounds and starting Skills.
 *
 * RULE DCC-BG-001 - Crawlers & Customization pp.6-11.
 * A crawler takes a background from each of four tables and picks two of that
 * background's three Skills. Human crawlers use Tables 1-4; animal crawlers use
 * Tables 5-8. Animal tables offer six Skills per background rather than three,
 * so the number offered is read from the content, not assumed.
 *
 * RULE DCC-BG-002 - the starting Rank depends on the table:
 * Childhood and Adolescence Rank 1, Hobby Rank 2, Career Rank 3
 * (C&C pp.7, 8, 9). Animal: Youth and Training Rank 1, Quirk Rank 2,
 * Adult Rank 3 (C&C pp.10, 11).
 *
 * RULE DCC-BG-003 - Crawlers & Customization p.6. No double-dipping: the same
 * Skill may not be taken from more than one background, because Ranks from
 * different backgrounds are not added together.
 */

/**
 * How a Background reads in a list.
 *
 * A printed Background is identified by the face of the table's die it sits on,
 * which is how the book lists it. A custom Background sits on no face
 * (`roll: null`), so it is labelled rather than numbered. Inventing a number
 * would either collide with a printed entry or imply the die has more sides
 * than it has.
 *
 * Lives here rather than in a component so that every list of Backgrounds reads
 * the same way and the "null." bug cannot come back in a second place.
 */
export function backgroundLabel(background: Background): string {
  return background.roll === null
    ? `Custom \u2014 ${background.name}`
    : `${background.roll}. ${background.name}`;
}

/** One table's worth of player choice. */
export interface BackgroundSelection {
  tableId: string;
  backgroundId: string;
  /** The Skill IDs the player picked from that background. */
  skillIds: string[];
}

export interface StartingSkill {
  skillId: string;
  rank: number;
  /** Where the Skill came from, for display and auditing. */
  origin: string;
}

export interface SelectionProblem {
  tableId: string;
  message: string;
}

export interface BackgroundValidation {
  valid: boolean;
  problems: SelectionProblem[];
}

export interface BackgroundLookup {
  table(id: string): BackgroundTable | undefined;
  background(id: string): Background | undefined;
}

/**
 * Validates a complete set of background selections.
 * Reports every problem rather than stopping at the first, so the creator can
 * show the player everything that needs fixing.
 */
export function validateBackgroundSelections(
  selections: readonly BackgroundSelection[],
  requiredTables: readonly BackgroundTable[],
  lookup: BackgroundLookup,
): BackgroundValidation {
  const problems: SelectionProblem[] = [];

  for (const table of requiredTables) {
    const selection = selections.find((s) => s.tableId === table.id);
    if (!selection) {
      problems.push({ tableId: table.id, message: `No ${table.label} background chosen.` });
      continue;
    }
    const background = lookup.background(selection.backgroundId);
    if (!background) {
      problems.push({ tableId: table.id, message: `Unknown background "${selection.backgroundId}".` });
      continue;
    }
    if (background.tableId !== table.id) {
      problems.push({
        tableId: table.id,
        message: `"${background.name}" does not belong to ${table.label}.`,
      });
      continue;
    }
    const unique = new Set(selection.skillIds);
    if (unique.size !== selection.skillIds.length) {
      problems.push({ tableId: table.id, message: `The same Skill was picked twice from "${background.name}".` });
    }
    if (unique.size !== table.chooseCount) {
      problems.push({
        tableId: table.id,
        message: `Choose exactly ${table.chooseCount} Skills from "${background.name}" (${unique.size} chosen).`,
      });
    }
    for (const skillId of unique) {
      if (!background.skillIds.includes(skillId)) {
        problems.push({
          tableId: table.id,
          message: `"${skillId}" is not offered by "${background.name}".`,
        });
      }
    }
  }

  // RULE DCC-BG-003 - no double-dipping across tables.
  const seen = new Map<string, string>();
  for (const selection of selections) {
    for (const skillId of selection.skillIds) {
      const previous = seen.get(skillId);
      if (previous && previous !== selection.tableId) {
        problems.push({
          tableId: selection.tableId,
          message: `"${skillId}" was already taken from another background. Ranks from different backgrounds are not added together (C&C p.6).`,
        });
      } else {
        seen.set(skillId, selection.tableId);
      }
    }
  }

  return { valid: problems.length === 0, problems };
}

/** Which Skills a set of selections grants, and at what Rank. */
export function startingSkillsFromBackgrounds(
  selections: readonly BackgroundSelection[],
  lookup: BackgroundLookup,
): StartingSkill[] {
  const result: StartingSkill[] = [];
  for (const selection of selections) {
    const table = lookup.table(selection.tableId);
    const background = lookup.background(selection.backgroundId);
    if (!table || !background) continue;
    for (const skillId of selection.skillIds) {
      result.push({ skillId, rank: table.startingRank, origin: `${table.label}: ${background.name}` });
    }
  }
  return result;
}

/** Skills already claimed by earlier tables, for greying out in the UI. */
export function skillsAlreadyTaken(
  selections: readonly BackgroundSelection[],
  exceptTableId: string,
): Set<string> {
  const taken = new Set<string>();
  for (const selection of selections) {
    if (selection.tableId === exceptTableId) continue;
    for (const skillId of selection.skillIds) taken.add(skillId);
  }
  return taken;
}
